import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight, History, Star } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Skeleton from '../ui/Skeleton';
import ChangeLogTable from './ChangeLogTable';
import ReactivateModal from './ReactivateModal';
import { getEntityHistory } from '../../utils/api';
import { actionBadge, formatDateTime } from '../../utils/commandCenterHelpers';

export default function VersionHistoryModal({ open, entityId, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(null);
  const [versions, setVersions] = useState([]);
  const [openRows, setOpenRows] = useState(() => new Set());
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await getEntityHistory(entityId);
      setEntity(res.data.entity);
      setVersions(res.data.versions);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    if (open) {
      setOpenRows(new Set());
      load();
    }
  }, [open, load]);

  if (!open) return null;

  const toggleRow = (id) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ReactivateModal expects an entity descriptor with entityType/entityRef/label/name.
  const entityDescriptor = entity
    ? { entityType: entity.entityType, entityRef: entity.entityRef, label: entity.label, name: entity.name }
    : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={entity ? entity.name : 'Version history'}
        description={entity ? `${entity.label} · ${entity.totalVersions} versions · current V${entity.currentVersionNumber ?? '—'}` : null}
        icon={History}
        size="xl"
      >
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        ) : (
          <ol className="relative ml-1.5 border-l border-slate-200 space-y-2.5 pl-4">
            {versions.map((v) => {
              const badge = actionBadge(v.action);
              const isOpen = openRows.has(v.id);
              return (
                <li key={v.id} className="relative">
                  <span
                    className={`absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                      v.isCurrent ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
                  />
                  <div className="rounded-lg border border-slate-200 bg-white">
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button type="button" onClick={() => toggleRow(v.id)} className="flex items-center gap-2 min-w-0 flex-1 text-left">
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                        <span className="text-[13px] font-semibold text-slate-900 tabular-nums">V{v.versionNumber}</span>
                        {v.isCurrent && (
                          <Badge variant="success" size="sm"><Star className="h-2.5 w-2.5" /> Current</Badge>
                        )}
                        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                        <span className="text-[12px] text-slate-500 truncate">{v.summary}</span>
                      </button>
                      <span className="text-[11.5px] text-slate-400 shrink-0 hidden sm:block">{v.savedByLabel}</span>
                      {!v.isCurrent && (
                        <Button size="xs" variant="outline" leftIcon={History} onClick={() => setTarget(v)}>
                          Reactivate
                        </Button>
                      )}
                    </div>
                    <div className="px-3 pb-2 flex items-center gap-3 text-[11px] text-slate-400 border-t border-slate-50 pt-1.5">
                      <span>{formatDateTime(v.savedAt)}</span>
                      <span>·</span>
                      <span>{v.changeCount} change row{v.changeCount === 1 ? '' : 's'}</span>
                    </div>
                    {isOpen && (
                      <div className="px-3 pb-3">
                        <ChangeLogTable changeLogs={v.changeLogs} />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Modal>

      <ReactivateModal
        open={!!target}
        onClose={() => setTarget(null)}
        entity={entityDescriptor}
        versions={versions}
        target={target}
        onDone={(data) => {
          if (data?.versions) {
            setVersions(data.versions);
            setEntity(data.entity);
          } else {
            load();
          }
          onChanged?.();
        }}
      />
    </>
  );
}
